import React, {
  ForwardRefRenderFunction,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Drawer, Modal } from 'antd';
import Form, { FormSchema } from '@sezenta/antd-schema-form';
import { useConnection } from '../../connection/connection';
import { VendorAddress } from '../../dto/Vendor';
import { Countries } from '../../common/SelectEnums';
import { useBusinessContext } from '../../context/BusinessContext';

export type AddressEditModalInstance = {
  open: (address?: VendorAddress) => Promise<VendorAddress>;
};

type Props = {};

const AddressEditModal: ForwardRefRenderFunction<
  AddressEditModalInstance,
  Props
> = (props, ref) => {
  const [title, setTitle] = useState<string>('');
  const [visible, setVisible] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [form] = Form.useForm<VendorAddress>();
  const connection = useConnection();
  const business = useBusinessContext();
  const [promiseCallback, setPromiseCallback] = useState<{
    resolve: (address: VendorAddress) => void;
    reject: (error: any) => void;
  }>();

  useImperativeHandle(
    ref,
    () => ({
      open: (address?: VendorAddress) => {
        setVisible(true);
        setSaving(false);
        form.resetFields();
        form.setFieldsValue(address ?? { country: business.country });
        return new Promise<VendorAddress>((resolve, reject) => {
          setPromiseCallback({ resolve, reject });
        });
      },
    }),
    [business, form],
  );

  const schema = useMemo(() => {
    const formSchema: FormSchema = [
      {
        id: 'attention',
        type: 'string',
        name: 'Attention',
        options: {
          rules: [{ required: true, message: 'Attention is required' }],
        },
      },
      {
        id: 'country',
        type: 'select2',
        name: 'Country',
        props: {
          options: Countries,
          showSearch: true,
          optionFilterProp: 'label',
        },
        options: {
          rules: [{ required: true, message: 'Country is required' }],
        },
      },
      {
        id: 'city',
        type: 'string',
        name: 'City',
        options: {
          rules: [{ required: true, message: 'City is required' }],
        },
      },
      {
        id: 'address',
        type: 'text',
        name: 'Address',
        options: {
          rules: [{ required: true, message: 'address is required' }],
        },
      },
      {
        id: 'state',
        type: 'string',
        name: 'State/Province',
        options: {
          rules: [{ required: true, message: 'State/Province is required' }],
        },
      },
      {
        id: 'zipCode',
        type: 'string',
        name: 'Zip/Postal Code',
      },
      {
        id: 'phone',
        type: 'phone',
        name: 'Phone',
        props: { defaultCountry: 'LK' },
        options: {
          rules: [{ validator: 'phone', message: 'Invalid phone number' }],
        },
      },
    ];
    return formSchema;
  }, []);

  const submit = useCallback(
    (values: VendorAddress) => {
      promiseCallback?.resolve(values);
      setVisible(false);
    },
    [promiseCallback],
  );

  const close = useCallback(() => {
    setVisible(false);
    promiseCallback?.reject('Cancelled');
  }, [promiseCallback]);

  return (
    <Drawer
      title={title}
      open={visible}
      onClose={close}
      destroyOnClose={true}
      footer={
        <div className="flex justify-between gap-1">
          <div className="w-full flex justify-end gap-1">
            <Button onClick={close}>Cancel</Button>
            <Button type="primary" loading={saving} onClick={form.submit}>
              OK
            </Button>
          </div>
        </div>
      }
      width={600}
    >
      <Form onFinish={submit} form={form} layout="vertical">
        <Form.Items schema={schema} />
      </Form>
    </Drawer>
  );
};

export const useAddressEditModal = () => useRef<AddressEditModalInstance>(null);

export default React.forwardRef<AddressEditModalInstance, Props>(
  AddressEditModal,
);
